import * as React from "react";
import {
  List,
  Datagrid,
  EditButton,
  CreateButton,
  TopToolbar,
  FunctionField,
} from "react-admin";
import { Chip, Stack, Typography } from "@mui/material";
import { useClub } from "../club/ClubContext";

const Actions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

// kleine Helper für RRULE → lesbar
function formatRRule(rrule?: string | null) {
  if (!rrule) return "Keine";

  if (rrule.includes("DAILY")) return "Täglich";
  if (rrule.includes("WEEKLY")) return "Wöchentlich";
  if (rrule.includes("MONTHLY")) return "Monatlich";

  return "Wiederholend";
}

function normalizeDescription(desc?: string | null): string {
  return String(desc ?? "")
    .replace(/\\n/g, "\n") // "\n" → echte Zeilenumbrüche
    .replace(/\r\n/g, "\n") // Windows normalize
    .replace(/\\,/g, ",")
    .trim();
}

function formatDuration(minsRaw: any): string {
  const mins = Number(minsRaw);
  if (!Number.isFinite(mins) || mins <= 0) return "–";

  // < 24h: wie bisher in Minuten anzeigen + zusätzlich Stunden/Min
  if (mins < 24 * 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h <= 0) return `${mins} Min`;
    if (m === 0) return `${mins} Min (${h}h)`;
    return `${mins} Min (${h}h ${m}m)`;
  }

  // >= 24h: in Tagen/Stunden/Minuten anzeigen + Minuten beibehalten
  const days = Math.floor(mins / (24 * 60));
  const rest = mins % (24 * 60);
  const hours = Math.floor(rest / 60);
  const m = rest % 60;

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (m) parts.push(`${m}m`);

  return `${parts.join(" ")} (${mins} Min)`;
}

export function EventSeriesList(props: any) {
  const { clubId } = useClub();

  return (
    <List
      {...props}
      actions={<Actions />}
      perPage={25}
      filter={clubId ? { club_id: clubId } : {}}
      sort={{ field: "starts_at", order: "ASC" }}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        {/* 🟦 Titel + Beschreibung */}
        <FunctionField
          label="Terminserie"
          render={(record: any) => (
            <Stack spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                {record.title}
              </Typography>
              {record.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.3, whiteSpace: "pre-line" }}
                >
                  {normalizeDescription(record.description)}
                </Typography>
              )}
            </Stack>
          )}
        />

        {/* 🟩 Status / Scope / Wiederholung */}
        <FunctionField
          label="Eigenschaften"
          render={(record: any) => (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                color={record.is_active ? "success" : "default"}
                label={record.is_active ? "Aktiv" : "Inaktiv"}
              />

              <Chip
                size="small"
                variant="outlined"
                label={record.scope === "ALL_CLUB" ? "Ganzer Verein" : "Team-spezifisch"}
              />

              <Chip
                size="small"
                variant="outlined"
                label={`Wiederholung: ${formatRRule(record.rrule)}`}
              />
            </Stack>
          )}
        />

        {/* 🕒 Meta */}
        <FunctionField
          label="Details"
          render={(record: any) => (
            <Stack spacing={0.25}>
              <Typography variant="body2">
                Dauer: {formatDuration(record.duration_minutes)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Zeitzone: {record.timezone}
              </Typography>
            </Stack>
          )}
        />

        {/* ✏️ Action */}
        <EditButton />
      </Datagrid>
    </List>
  );
}
